/**
 * Redy Capital — motor de precalificación.
 *
 * Redy NO presta y NO decide. Este módulo proyecta las ventas anuales del
 * negocio a partir del historial que Redy sí tiene y las cruza contra los
 * mínimos publicados de SmallBusinessLoans (marketplace de crédito de North,
 * "SBL Product Matrix 2025") para decirle al dueño qué opciones tiene sentido
 * que explore.
 *
 * ── Por qué se proyecta en vez de sumar 12 meses ──────────────────────────
 *
 * SBL pide "ventas brutas anuales", pero Redy es joven: al 2026-08-08 el
 * merchant con más historial llevaba 86 días y la mayoría entre 38 y 73. Sumar
 * la ventana disponible y llamarla "ventas anuales" produce un número falso a
 * la baja que descalifica negocios sanos — medido: Zulianisimo mostraba
 * $111,129 (39 días) cuando su corrida anual va en ~$1.04M, la diferencia entre
 * "solo capital de trabajo" y "los cinco productos".
 *
 * Proyectar es además lo que hace el propio prestamista: SBL pide 3 meses de
 * estados de cuenta, no 12.
 *
 * 🔴 Pero proyectar sin piso miente peor. Con 1 día de historial, un merchant
 * medido ($2,801 en su primer día) proyectaba $1,022,365 anuales. Por eso
 * `MIN_DAYS_FOR_PROJECTION`: por debajo de ese umbral NO se proyecta y NO se
 * personaliza — la página cae a modo informativo. Un número inventado con cara
 * de precisión es peor que no mostrar número.
 *
 * ── La antigüedad NO se evalúa ────────────────────────────────────────────
 *
 * SBL exige antigüedad del NEGOCIO (3 a 24 meses según el producto). Redy solo
 * conoce la antigüedad EN REDY, que no es lo mismo: Zulianisimo llevaba 1 mes
 * en la plataforma y años operando. Como el dato que tenemos no responde la
 * pregunta que se hace, este módulo no emite veredicto sobre ese eje — la UI
 * muestra el requisito como informativo y el prestamista lo verifica.
 *
 * ── Regla de honestidad ───────────────────────────────────────────────────
 *
 * El estado máximo se llama `meets` ("cumple el mínimo de ventas") y NUNCA
 * `approved`. Falta el FICO del dueño (550–660 según producto), 3 meses de
 * estados de cuenta y una solicitud firmada. El copy dice "cumples el mínimo
 * para ser evaluado", jamás "calificas para $X".
 *
 * No es cosmético: prometer monto o aprobación convierte una página informativa
 * en publicidad de crédito con exposición bajo FTC Act §5 y las leyes estatales
 * de disclosure de financiamiento comercial (CA SB 1235, NY CFDL, UT, VA).
 *
 * Ojo con el producto de al lado: Clover Capital (Fiserv) es un MCA — el
 * procesador compra un % de ventas futuras — y por eso SÍ puede prometer "sin
 * garantía personal / sin impacto en tu score / sin intereses". SBL es otra
 * cosa (préstamos de terceros). No copies esas promesas.
 */

export type CapitalProductId =
  | "working_capital"
  | "term_loan"
  | "line_of_credit"
  | "equipment"
  | "sba"

export interface CapitalProduct {
  id: CapitalProductId
  /** Mínimo de ventas brutas anuales, en centavos. Este eje SÍ lo medimos. */
  minAnnualSalesCents: number
  /** FICO mínimo — lo evalúa el prestamista. Documental, Redy no lo ve. */
  minFico: number
  /**
   * Antigüedad mínima del NEGOCIO, en meses. Informativo: Redy solo conoce la
   * antigüedad en la plataforma, que no responde esta pregunta. No se evalúa.
   */
  minMonths: number
  /** Rango de financiamiento, en centavos. Para mostrar, nunca para prometer. */
  amountMinCents: number
  amountMaxCents: number
}

/**
 * Días de historial que Redy necesita para estimar la corrida anual, contados
 * desde el PRIMER PAGO REAL del negocio — no desde que se creó la locación,
 * que puede existir semanas antes de que se venda el primer plato.
 *
 * 90 días = los "3 meses de estados de cuenta" que pide el propio prestamista.
 * Un trimestre completo absorbe el arranque, las pruebas y un fin de semana
 * atípico, cosas que a los dos meses todavía distorsionan la proyección.
 *
 * 🔴 Este umbral gobierna NUESTRA ESTIMACIÓN, no el acceso del comercio al
 * crédito. La antigüedad que evalúa SBL es la del NEGOCIO, y un restaurante
 * con años abierto que entró a Redy el mes pasado ya la cumple de sobra. Por
 * eso la página mantiene vivo el CTA durante toda la cuenta regresiva: a los X
 * días lo que falta es que Redy pueda poner un número, no que el dueño pueda
 * solicitar. Bloquear el botón sería falso y además mataría el referido.
 */
export const MIN_DAYS_FOR_PROJECTION = 90

/** Progreso hacia el desbloqueo de la estimación. */
export interface CapitalCountdown {
  /** Días desde el primer pago, tope en MIN_DAYS_FOR_PROJECTION. */
  daysElapsed: number
  /** Días que faltan. 0 cuando ya desbloqueó. */
  daysRemaining: number
  /** Avance 0–100, para la barra. */
  percent: number
  unlocked: boolean
}

/**
 * Traduce días de historial a un progreso mostrable.
 * Un `daysOfHistory` inválido (NaN, negativo) cuenta como 0 — día cero, nunca
 * como desbloqueado.
 */
export function getCapitalCountdown(daysOfHistory: number): CapitalCountdown {
  const days = safe(daysOfHistory)
  const elapsed = Math.min(days, MIN_DAYS_FOR_PROJECTION)
  const remaining = Math.max(0, MIN_DAYS_FOR_PROJECTION - days)
  return {
    daysElapsed: elapsed,
    daysRemaining: remaining,
    percent: Math.round((elapsed / MIN_DAYS_FOR_PROJECTION) * 100),
    unlocked: remaining === 0,
  }
}

/**
 * Matriz SBL 2025, ordenada de más accesible a más exigente. El orden importa:
 * es el que ve el dueño, y lo primero que lea debe ser lo que sí alcanza.
 */
export const CAPITAL_PRODUCTS: readonly CapitalProduct[] = [
  {
    id: "working_capital",
    minAnnualSalesCents: 50_000_00,
    minFico: 550,
    minMonths: 3,
    amountMinCents: 3_000_00,
    amountMaxCents: 600_000_00,
  },
  {
    id: "term_loan",
    minAnnualSalesCents: 125_000_00,
    minFico: 550,
    minMonths: 6,
    amountMinCents: 5_000_00,
    amountMaxCents: 750_000_00,
  },
  {
    id: "line_of_credit",
    minAnnualSalesCents: 180_000_00,
    minFico: 500,
    minMonths: 24,
    amountMinCents: 5_000_00,
    amountMaxCents: 500_000_00,
  },
  {
    id: "equipment",
    minAnnualSalesCents: 250_000_00,
    minFico: 600,
    minMonths: 24,
    amountMinCents: 5_000_00,
    amountMaxCents: 1_000_000_00,
  },
  {
    id: "sba",
    minAnnualSalesCents: 250_000_00,
    minFico: 660,
    minMonths: 12,
    amountMinCents: 50_000_00,
    amountMaxCents: 500_000_00,
  },
] as const

/** Lo que Redy observa del negocio. Nada de esto es PII crediticia. */
export interface CapitalSnapshot {
  /** Ventas brutas efectivamente vistas en Redy, en centavos. */
  observedSalesCents: number
  /** Días transcurridos desde el primer pedido pagado hasta hoy. */
  daysOfHistory: number
}

/** Normaliza a un número finito > 0; NaN / negativos / null → 0. */
function safe(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Proyecta ventas anuales desde el historial observado.
 * Devuelve `null` cuando no hay historial suficiente — el llamador DEBE tratar
 * ese null como "no personalizar", nunca como cero.
 */
export function projectAnnualSalesCents(snapshot: CapitalSnapshot): number | null {
  const days = safe(snapshot.daysOfHistory)
  const observed = safe(snapshot.observedSalesCents)
  if (days < MIN_DAYS_FOR_PROJECTION) return null
  return Math.round((observed / days) * 365)
}

export type CapitalStatus =
  /** Alcanza el mínimo de ventas anuales. NO es aprobación. */
  | "meets"
  /** No alcanza el mínimo de ventas anuales. */
  | "below_sales"

export interface CapitalEligibility {
  product: CapitalProduct
  status: CapitalStatus
  /** Cuánto falta de venta anual proyectada (centavos). 0 si ya alcanza. */
  salesGapCents: number
}

/**
 * Cruza la proyección anual contra el mínimo de ventas de cada producto.
 *
 * Solo evalúa el eje de ventas. La antigüedad del negocio y el FICO los
 * verifica el prestamista — ver el encabezado del módulo.
 */
export function evaluateCapitalEligibility(projectedAnnualSalesCents: number): CapitalEligibility[] {
  const sales = safe(projectedAnnualSalesCents)

  return CAPITAL_PRODUCTS.map((product) => {
    const salesGapCents = Math.max(0, product.minAnnualSalesCents - sales)
    return {
      product,
      status: salesGapCents > 0 ? ("below_sales" as const) : ("meets" as const),
      salesGapCents,
    }
  })
}

/** Cuántos productos alcanzan el mínimo de ventas. Para el titular de la página. */
export function countMeeting(results: CapitalEligibility[]): number {
  return results.filter((r) => r.status === "meets").length
}
